import React from "react";

import { useList, useNavigation } from "@refinedev/core";
import type { CrudFilters, GetListResponse } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { ExternalLink } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMembers } from "@/contexts/member-context";
import { isRefreshHitlReason } from "@/lib/tina/constants";
import {
  ENRICHMENT_CONTENT_TYPE_OPTIONS,
  SCRAPE_CONTENT_TYPE_OPTIONS,
} from "@/lib/tina/content-types";
import { ENRICHMENT_CLASS_SENTINEL } from "@/lib/tina/enrichment-jobs";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import {
  type ItemsColumnVariant,
  useItemsColumns,
} from "@/pages/tina/items/hooks/use-items-columns";
import {
  CRON_TRIGGERER,
  buildItemsFilters,
  isEnrichmentView,
  listsHitlRows,
} from "@/pages/tina/items/items-filters";
import { runOptionLabel } from "@/pages/tina/lib/run-attribution";
import { countryFlag } from "@/pages/tina/runs/components/site-picker/utils";
import { useTinaSites } from "@/pages/tina/runs/hooks/use-tina-sites";
import type { PipelineItem, RunRead } from "@/types/tina";

const ALL_COUNTRIES = "__all_countries__";
const ALL_TRIGGERERS = "__all_triggerers__";
const ALL_RUNS = "__all_runs__";
const ALL_CONTENT_TYPES = "__all_content_types__";

/**
 * Takes the refresh class back out of a `status=hitl` page.
 *
 * `list_events` widens the class filter to `scrape_class_only() | refresh_class_only()` for
 * exactly one listing — `status=hitl` with no `content_type` (ruling R34) — which is the query
 * `/tina/hitl` mounts. The owner has since ruled that refresh reviews get their own surface, and
 * `GET /events` has **no** way to say "every class except refresh": `content_type` is a single
 * exact value (or one of the two class sentinels) and `hitl_reason` is single-value equality with
 * no negation. So the re-narrowing happens here, on the page, as close to the source as the API
 * allows.
 *
 * **`total` is deliberately left alone.** It is the server's count over the un-narrowed WHERE, so
 * a hidden row still owns its slot in the offsets Refine pages through — reducing it would shrink
 * `pageCount` below the number of server pages and make the last page unreachable, silently losing
 * curator work. The consequence is a page that can be short and a footer count that includes the
 * hidden rows. A `RefreshReviewNotice` banner used to reconcile the two on `/tina/hitl`; the owner
 * had it removed on 2026-09-07, so the discrepancy is now unexplained on the page by choice — the
 * count lives on `/tina/home` beside the link to `/tina/refresh-review`.
 *
 * A no-op once Tina drops the R34 widening (see the follow-up on `list_events`) — at which point
 * this whole option can go.
 */
const HIDE_REFRESH_ROWS: {
  select: (
    response: GetListResponse<PipelineItem>,
  ) => GetListResponse<PipelineItem>;
} = {
  // Module-level, so the identity is stable across renders and the query is not
  // re-created on every one.
  select: (response) => ({
    ...response,
    data: response.data.filter(
      (item) => !isRefreshHitlReason(item.hitl_reason),
    ),
  }),
};

interface ItemsListProps {
  initialFilters?: CrudFilters;
  title?: string;
  /**
   * Rendered under the header, above the table — a page-level disclosure the
   * generic list itself has no opinion about (e.g. the HITL queue naming the
   * refresh reviews it hides).
   */
  notice?: React.ReactNode;
  /**
   * When set, the country + discover-seed toggles persist to localStorage under
   * this key so the view is not reset after navigating into an item and back.
   */
  persistKey?: string;
  /**
   * Forces the job column set on regardless of what is selected.
   *
   * The variant normally FOLLOWS the content-type select — picking any
   * enrichment flow, or "All enrichment jobs", swaps in the job columns and
   * drops the two toolbar controls that are meaningless for job rows: every
   * job is `system-enrichment`, so Country never narrows
   * anything, and no job is a discover seed. This prop only pins that on for a
   * caller that mounts a permanently-enrichment preset; no caller does today.
   */
  variant?: ItemsColumnVariant;
  /**
   * Initial value of the content-type select, before a curator changes it.
   * Persisted per `persistKey` like every other toolbar control.
   */
  defaultContentType?: string | null;
  /**
   * Which content-type classes the select may reach.
   *
   * `"scrape"` is not cosmetic. `/tina/hitl` mounts this component under the
   * title "HITL queue", and enrichment parks are ruled to have their **own**
   * surface — Tina hides them from an un-opted-in `GET /events`
   * (`scrape_class_only()`) and `metric_hitl_backlog` excludes their reasons, so
   * offering the enrichment group there both puts parks in a queue that is
   * scrape-only by design and leaves a queue titled "HITL queue" filtered to
   * something else. Worse, the selection persists to localStorage, so it
   * survives the session — the stuck-filter failure `buildItemsFilters` goes out
   * of its way to prevent for run and country.
   */
  contentTypeScope?: "all" | "scrape";
  /**
   * Renders an "Open all in tabs" button in the header that opens the current
   * page's rows in one new browser tab each — curators asked for this on the
   * HITL queue specifically, so no other caller passes it.
   */
  enableOpenAllInTabs?: boolean;
}

export const ItemsList = ({
  initialFilters,
  title = "Items",
  notice,
  persistKey,
  variant = "default",
  defaultContentType = null,
  contentTypeScope = "all",
  enableOpenAllInTabs = false,
}: ItemsListProps) => {
  const { show } = useNavigation();
  const [showSeeds, setShowSeeds] = usePersistedState<boolean>(
    persistKey ? `tina-items:${persistKey}:show-seeds` : null,
    false,
  );
  const [country, setCountry] = usePersistedState<string | null>(
    persistKey ? `tina-items:${persistKey}:country` : null,
    null,
  );
  const [triggeredBy, setTriggeredBy] = usePersistedState<string | null>(
    persistKey ? `tina-items:${persistKey}:triggered-by` : null,
    null,
  );
  const [runId, setRunId] = usePersistedState<string | null>(
    persistKey ? `tina-items:${persistKey}:run` : null,
    null,
  );
  const [persistedContentType, setContentType] = usePersistedState<
    string | null
  >(
    persistKey ? `tina-items:${persistKey}:content-type` : null,
    defaultContentType,
  );

  // Neutralised, not merely un-offered — the same treatment country and Cron get
  // below, and for the same reason: a value written to localStorage while the
  // group was on screen would otherwise keep filtering a scrape-scoped mount
  // with no control left to clear it, across sessions.
  const scrapeOnly = contentTypeScope === "scrape";
  const contentType =
    scrapeOnly && isEnrichmentView(persistedContentType)
      ? null
      : persistedContentType;

  // The column preset follows the selection. Until this line the `enrichment`
  // variant was unreachable — nothing passed the prop — so picking "Artist
  // enrichment" rendered job rows through the scrape columns: Site always
  // `system-enrichment`, Context "—", Confidence "—" on every row.
  const isEnrichment =
    variant === "enrichment" || isEnrichmentView(contentType);
  const effectiveVariant: ItemsColumnVariant = isEnrichment
    ? "enrichment"
    : "default";
  // A `status=hitl` read is the one listing Tina widens to the refresh class,
  // so it is the one listing that has to narrow itself back. Derived from the
  // caller's pinned filters rather than taken as a prop; see `listsHitlRows`.
  const hidesRefreshRows = listsHitlRows(initialFilters);
  const columns = useItemsColumns({ variant: effectiveVariant });
  // Both run dropdowns list runs of the class being viewed. `job_kind` is
  // `GET /runs`' own param (these two queries hit `/runs`, not `/events`), and
  // all 949 job rows in prod live in `job_kind=enrichment` runs — so a
  // hard-coded `scrape` left the Run filter unable to name a single run an
  // enrichment job belongs to.
  const runJobKind = isEnrichment ? "enrichment" : "scrape";

  const { members } = useMembers();
  // One class at a time. Every external enrichment job gets its OWN `runs` row
  // (one per intake request or fan-out batch), and enrichment overtook scrapes
  // in prod on 2026-08-27 — 82 enrichment runs against 42 scrape runs in 48 h.
  // Left unfiltered, two thirds of this dropdown is single-job enrichment runs
  // nobody picks while looking at scrape events.
  const recentRunsQ = useList<RunRead>({
    resource: "tina-runs",
    pagination: { pageSize: 50 },
    filters: [{ field: "job_kind", operator: "eq", value: runJobKind }],
    sorters: [{ field: "created_at", order: "desc" }],
  });
  const recentRuns = (recentRunsQ?.query?.data?.data ?? []) as RunRead[];

  // Only humans who actually triggered a run should appear in the "triggerer"
  // filter — not every admin. Cron runs carry a null triggerer, so we derive
  // the set from recent *manual* runs (the human-triggered kind).
  const manualRunsQ = useList<RunRead>({
    resource: "tina-runs",
    pagination: { pageSize: 100 },
    filters: [
      { field: "trigger_type", operator: "eq", value: "manual" },
      // Every enrichment run is `trigger_type=manual` too (there is no
      // enrichment trigger type), so without this the triggerer set is drawn
      // from the enrichment fleet as much as from real human triggers.
      { field: "job_kind", operator: "eq", value: runJobKind },
    ],
    sorters: [{ field: "created_at", order: "desc" }],
  });
  const triggererMembers = React.useMemo(() => {
    const uids = new Set(
      ((manualRunsQ?.query?.data?.data ?? []) as RunRead[])
        .map((r) => r.triggered_by)
        .filter((id): id is string => Boolean(id)),
    );
    // Keep a persisted selection visible even if it has aged out of the window.
    if (triggeredBy && triggeredBy !== CRON_TRIGGERER) uids.add(triggeredBy);
    return members.filter((m) => uids.has(m.id));
  }, [manualRunsQ?.query?.data?.data, members, triggeredBy]);

  const { sites } = useTinaSites();
  const countryOptions = React.useMemo(
    () =>
      Array.from(
        new Set(sites.map((site) => site.country_code).filter(Boolean)),
      ).sort(),
    [sites],
  );

  // Every user-controlled filter is emitted on every render (cleared ones as
  // value: undefined, not omitted) so Refine's unionFilters can shadow any
  // stale internal copy — see buildItemsFilters for the full rationale.
  const filters = buildItemsFilters({
    initialFilters,
    // Both hidden controls are neutralised rather than merely un-rendered: a
    // value persisted under this key before the variant changed would otherwise
    // keep filtering with no control left to clear it.
    country: isEnrichment ? null : country,
    runId,
    // Same neutralisation, same reason: every enrichment run is
    // `trigger_type=manual` (there is no enrichment trigger type), so a Cron
    // selection carried over from a scrape view would filter the class down to
    // nothing with the option that set it no longer on screen.
    triggeredBy:
      isEnrichment && triggeredBy === CRON_TRIGGERER ? null : triggeredBy,
    // A job row is never a discover seed, so the toggle has nothing to hide in
    // the enrichment variant — emitted as "show" so the predicate is dropped.
    showSeeds: isEnrichment ? true : showSeeds,
    contentType,
  });

  const table = useTable<PipelineItem>({
    columns,
    initialState: {
      columnPinning: {
        right: ["actions"],
      },
    },
    refineCoreProps: {
      resource: "tina-items",
      pagination: { pageSize: 50 },
      filters: { permanent: filters },
      ...(hidesRefreshRows ? { queryOptions: HIDE_REFRESH_ROWS } : {}),
    },
    meta: {
      onDoubleClick: (
        _e: React.MouseEvent,
        row: { original: PipelineItem },
      ) => {
        show("tina-items", row.original.id);
      },
    },
  });

  // The rows the curator is actually looking at right now — same accessor
  // `DataTable` renders from, so "visible" here matches what's on screen
  // (the current page, post-filter) rather than every row the query could
  // page through.
  const visibleRows = table.reactTable.getRowModel().rows;
  const handleOpenAllInTabs = () => {
    // Only the first tab per click gets through until the curator grants the
    // site "allow pop-ups and redirects" — a browser-level cap on how many
    // browsing contexts one user gesture may spawn. Nothing in JS gets around
    // it (a synthetic `<a target="_blank">` click was tried in Brave and
    // blocked identically), so the button's tooltip says so instead.
    for (const row of visibleRows) {
      window.open(`/tina/items/show/${row.original.id}`, "_blank");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">{title}</h1>
          {enableOpenAllInTabs && (
            <Button
              variant="outline"
              size="sm"
              disabled={visibleRows.length === 0}
              onClick={handleOpenAllInTabs}
              title="Opens every row on this page in its own tab. Your browser must allow pop-ups for this site, or only the first tab opens."
            >
              <ExternalLink className="h-4 w-4" />
              Open all in tabs
            </Button>
          )}
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={contentType ?? ALL_CONTENT_TYPES}
            onValueChange={(value) => {
              const next = value === ALL_CONTENT_TYPES ? null : value;
              // A run belongs to exactly one class, so a run id chosen in the
              // other one is a filter with no option left to clear it — the
              // list would sit empty with the Run select showing nothing.
              if (isEnrichmentView(next) !== isEnrichmentView(contentType)) {
                setRunId(null);
              }
              setContentType(next);
            }}
          >
            <SelectTrigger className="h-9 w-[200px]">
              <SelectValue placeholder="All scrape types" />
            </SelectTrigger>
            <SelectContent>
              {/* Cleared means "send no `content_type`", which is Tina's
                  scrape-class default — not "everything". Labelled
                  for what it does. Wherever both groups are offered they are
                  offered together: the select is the only way back, so hiding
                  either half strands a curator in the class they last picked. */}
              <SelectItem value={ALL_CONTENT_TYPES}>
                All scrape types
              </SelectItem>
              <SelectSeparator />
              {SCRAPE_CONTENT_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              {!scrapeOnly && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>Enrichment jobs</SelectLabel>
                    {/* Tina's class sentinel — one query matching all nine
                        flows. Before it, a curator asking "what is enriching
                        right now" had to page through nine separate
                        selections. */}
                    <SelectItem value={ENRICHMENT_CLASS_SENTINEL}>
                      All enrichment jobs
                    </SelectItem>
                    {ENRICHMENT_CONTENT_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
          {!isEnrichment && (
            <Select
              value={country ?? ALL_COUNTRIES}
              onValueChange={(value) =>
                setCountry(value === ALL_COUNTRIES ? null : value)
              }
            >
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="All countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_COUNTRIES}>All countries</SelectItem>
                {countryOptions.map((code) => {
                  const flag = countryFlag(code);
                  return (
                    <SelectItem key={code} value={code}>
                      {flag ? `${flag} ` : ""}
                      {code}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
          <Select
            value={triggeredBy ?? ALL_TRIGGERERS}
            onValueChange={(value) =>
              setTriggeredBy(value === ALL_TRIGGERERS ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[180px]">
              <SelectValue placeholder="Any triggerer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TRIGGERERS}>Any triggerer</SelectItem>
              {/* No enrichment run is a cron run — `runs.trigger_type` is
                  only ever manual/cron and enrichment intake always
                  writes `manual` (89 of 89 in prod), so the option could only
                  ever return an empty page here. */}
              {!isEnrichment && (
                <SelectItem value={CRON_TRIGGERER}>Cron</SelectItem>
              )}
              {triggererMembers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.displayName || m.email || m.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={runId ?? ALL_RUNS}
            onValueChange={(value) =>
              setRunId(value === ALL_RUNS ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[260px]">
              <SelectValue placeholder="Any run" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_RUNS}>Any run</SelectItem>
              {recentRuns.map((run) => (
                <SelectItem key={run.id} value={run.id}>
                  {runOptionLabel(run, members)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isEnrichment && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="show-seeds"
                checked={showSeeds}
                onCheckedChange={(v) => setShowSeeds(!!v)}
              />
              <Label htmlFor="show-seeds" className="text-sm">
                Show discover-seeds
              </Label>
            </div>
          )}
        </div>
      </div>
      {notice}
      <DataTable table={table} />
    </div>
  );
};
